import { Box, Button, Flex, FormControl, FormLabel, Heading, Input, Text, useColorModeValue, useToast, Spinner, Alert, AlertIcon } from '@chakra-ui/react';
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Custom components
import DefaultAuth from 'layouts/auth/variants/Default';
import AuthApi from 'api/auth';

// Assets
import illustration from 'assets/img/auth/auth.png';

function ResetPassword() {
	const textColor = useColorModeValue('navy.700', 'white');
	const textColorSecondary = 'gray.400';
	const brandStars = useColorModeValue('brand.500', 'brand.400');
	const toast = useToast();
	const navigate = useNavigate();
	const { token } = useParams<{ token: string }>();

	const [estado, setEstado] = React.useState<'validando' | 'valido' | 'invalido'>('validando');
	const [password, setPassword] = React.useState('');
	const [confirmPassword, setConfirmPassword] = React.useState('');
	const [isLoading, setIsLoading] = React.useState(false);
	const [completado, setCompletado] = React.useState(false);

	React.useEffect(() => {
		if (!token) {
			setEstado('invalido');
			return;
		}
		AuthApi.ValidateResetToken(token)
			.then(() => setEstado('valido'))
			.catch(() => setEstado('invalido'));
	}, [token]);

	const handleSubmit = async () => {
		if (!password || password.length < 6) {
			toast({
				title: 'Contraseña muy corta',
				description: 'La contraseña debe tener al menos 6 caracteres',
				status: 'warning', duration: 3000, isClosable: true,
			});
			return;
		}
		if (password !== confirmPassword) {
			toast({
				title: 'Las contraseñas no coinciden',
				status: 'warning', duration: 3000, isClosable: true,
			});
			return;
		}

		setIsLoading(true);
		try {
			await AuthApi.ResetPassword(token, password);
			setCompletado(true);
			toast({
				title: 'Contraseña actualizada',
				description: 'Ya puedes iniciar sesión con tu nueva contraseña.',
				status: 'success', duration: 4000, isClosable: true,
			});
			setTimeout(() => navigate('/auth/sign-in/default'), 2500);
		} catch (error: any) {
			toast({
				title: 'Error',
				description: error?.response?.data?.error || 'No se pudo restablecer la contraseña',
				status: 'error', duration: 4000, isClosable: true,
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<DefaultAuth illustrationBackground={illustration} image={illustration}>
			<Flex
				w='100%'
				maxW='max-content'
				mx={{ base: 'auto', lg: '0px' }}
				me='auto'
				h='100%'
				alignItems='start'
				justifyContent='center'
				mb={{ base: '30px', md: '60px', lg: '100px', xl: '60px' }}
				px={{ base: '25px', md: '0px' }}
				mt={{ base: '40px', lg: '16vh', xl: '22vh' }}
				flexDirection='column'>
				<Box me='auto' mb='34px'>
					<Heading color={textColor} fontSize={{ base: '3xl', md: '36px' }} mb='16px'>
						Restablecer contraseña
					</Heading>
					{estado === 'valido' && !completado && (
						<Text color={textColorSecondary} fontSize='md' w={{ base: '100%', lg: '456px' }} maxW='100%'>
							Ingresa tu nueva contraseña.
						</Text>
					)}
				</Box>

				{estado === 'validando' && (
					<Flex justify='center' w={{ base: '100%', lg: '456px' }}><Spinner size='lg' /></Flex>
				)}

				{estado === 'invalido' && (
					<Alert status='error' borderRadius='md' w={{ base: '100%', lg: '456px' }}>
						<AlertIcon />
						Este enlace es inválido o ha expirado. Solicita uno nuevo.
					</Alert>
				)}

				{estado === 'valido' && completado && (
					<Alert status='success' borderRadius='md' w={{ base: '100%', lg: '456px' }}>
						<AlertIcon />
						Contraseña actualizada. Redirigiendo al inicio de sesión...
					</Alert>
				)}

				{estado === 'valido' && !completado && (
					<Flex
						zIndex='2'
						direction='column'
						w={{ base: '100%', lg: '456px' }}
						maxW='100%'
						background='transparent'
						borderRadius='15px'
						mx={{ base: 'auto', lg: 'unset' }}
						me='auto'
						mb={{ base: '20px', md: 'auto' }}
						align='start'>
						<FormControl>
							<FormLabel display='flex' ms='4px' fontSize='sm' fontWeight='500' color={textColor} mb='8px'>
								Nueva contraseña<Text color={brandStars}>*</Text>
							</FormLabel>
							<Input
								isRequired={true}
								variant='auth'
								fontSize='sm'
								type='password'
								placeholder='Mínimo 6 caracteres'
								mb='24px'
								size='lg'
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
							<FormLabel display='flex' ms='4px' fontSize='sm' fontWeight='500' color={textColor} mb='8px'>
								Confirmar contraseña<Text color={brandStars}>*</Text>
							</FormLabel>
							<Input
								isRequired={true}
								variant='auth'
								fontSize='sm'
								type='password'
								placeholder='Repite la contraseña'
								mb='24px'
								size='lg'
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
							/>
							<Button
								fontSize='sm'
								variant='brand'
								fontWeight='500'
								w='100%'
								h='50'
								mb='24px'
								onClick={handleSubmit}
								isLoading={isLoading}
								loadingText='Guardando...'
							>
								Restablecer contraseña
							</Button>
						</FormControl>
					</Flex>
				)}
			</Flex>
		</DefaultAuth>
	);
}

export default ResetPassword;
